/**
 * Match Airbnb photo-tour room labels to property/checklist room names.
 */

import { matchRoom } from "@/lib/roomUtils";

export type ScrapedPhotoLike = { roomLabel: string | null };

export type AirbnbLabelMapping = {
  detectedLabel: string;
  photoCount: number;
  /** Property room name to assign photos to; null = skip photos with this label */
  propertyRoomName: string | null;
};

export function getDetectedLabelsFromPhotos(photos: ScrapedPhotoLike[]): string[] {
  const labels = new Set<string>();
  for (const p of photos) {
    const t = p.roomLabel?.trim();
    if (t) labels.add(t);
  }
  return [...labels].sort((a, b) => a.localeCompare(b));
}

function countPhotosByLabel(photos: ScrapedPhotoLike[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of photos) {
    const t = p.roomLabel?.trim();
    if (!t) continue;
    counts[t] = (counts[t] ?? 0) + 1;
  }
  return counts;
}

function suggestPropertyRoom(
  detectedLabel: string,
  propertyRoomNames: string[],
  usedPropertyRooms: Set<string>
): string | null {
  const rows = propertyRoomNames.map((name) => ({ id: name, name }));
  const { exact, suggested } = matchRoom(detectedLabel, rows);
  if (exact && !usedPropertyRooms.has(exact.name)) return exact.name;
  if (suggested && !usedPropertyRooms.has(suggested.name)) return suggested.name;
  return null;
}

export function buildInitialLabelMappings(
  photos: ScrapedPhotoLike[],
  propertyRoomNames: string[],
  /** All room names from the listing photo tour (includes rooms whose photos failed to parse). */
  listingRoomNames: string[] = []
): AirbnbLabelMapping[] {
  const counts = countPhotosByLabel(photos);
  const labelSet = new Set<string>(Object.keys(counts));
  for (const name of listingRoomNames) {
    const t = name?.trim();
    if (t) labelSet.add(t);
  }
  const labels = [...labelSet].sort((a, b) => a.localeCompare(b));
  const used = new Set<string>();
  return labels.map((detectedLabel) => {
    const propertyRoomName = suggestPropertyRoom(detectedLabel, propertyRoomNames, used);
    if (propertyRoomName) used.add(propertyRoomName);
    return {
      detectedLabel,
      photoCount: counts[detectedLabel] ?? 0,
      propertyRoomName,
    };
  });
}

/** Multiple listing labels mapped to the same property room (user should confirm). */
export function findDuplicatePropertyAssignments(
  mappings: AirbnbLabelMapping[]
): { propertyRoomName: string; labels: string[] }[] {
  const byRoom = new Map<string, string[]>();
  for (const m of mappings) {
    if (!m.propertyRoomName) continue;
    const list = byRoom.get(m.propertyRoomName) ?? [];
    list.push(m.detectedLabel);
    byRoom.set(m.propertyRoomName, list);
  }
  return [...byRoom.entries()]
    .filter(([, labels]) => labels.length > 1)
    .map(([propertyRoomName, labels]) => ({ propertyRoomName, labels }));
}

export function needsRoomMappingReview(
  mappings: AirbnbLabelMapping[],
  propertyRoomNames: string[]
): boolean {
  if (propertyRoomNames.length === 0) return false;
  if (mappings.length === 0) return false;
  if (mappings.length > propertyRoomNames.length) return true;
  if (mappings.some((m) => !m.propertyRoomName)) return true;
  if (findDuplicatePropertyAssignments(mappings).length > 0) return true;
  return false;
}

export function labelMappingsToRecord(
  mappings: AirbnbLabelMapping[]
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const m of mappings) out[m.detectedLabel] = m.propertyRoomName;
  return out;
}

/** Apply confirmed label → property room map to per-photo assignment indices. */
export function applyLabelMappingsToPhotoAssignments(
  photos: ScrapedPhotoLike[],
  labelToPropertyRoom: Record<string, string | null>
): Record<number, string> {
  const initial: Record<number, string> = {};
  photos.forEach((p, i) => {
    const label = p.roomLabel?.trim() ?? "";
    if (!label) {
      initial[i] = "";
      return;
    }
    const mapped = labelToPropertyRoom[label];
    initial[i] = mapped ?? "";
  });
  return initial;
}
